import * as RadixDialog from '@radix-ui/react-dialog';
import { useEffect, useRef } from 'react';

import { cn } from '../../utils/cn';
import type { DialogProps } from './Dialog.types';
import { DIALOG_WEB_SHADOW_CLASS } from './Dialog.variants';
import { DialogSurface } from './DialogSurface';

/**
 * Cross-platform Dialog — web implementation.
 *
 * Thin wrapper over `@radix-ui/react-dialog` (portal, focus trap, Escape and
 * click-outside dismissal, aria wiring). The visual panel lives in
 * `DialogSurface` (shared with `Dialog.native.tsx`); this file owns the
 * overlay, centering and entry/exit animation.
 *
 * The Content element is a full-viewport `pointer-events-none` centering
 * flexbox — gutter clicks fall through to the Overlay and dismiss, exactly
 * like a click anywhere else outside the panel.
 */
export function Dialog({
  open,
  onClose,
  onCloseButtonPress,
  onDismissed: _onDismissed, // native-only: a web dialog has no dismissal to wait for
  overlayClassName,
  className,
  subheading,
  accessibilityLabel,
  ...surface
}: DialogProps) {
  // Was the dialog open when the current press STARTED? Radix judges an
  // outside press on its pointerdown but dispatches the dismiss after the
  // click, and a closing dialog's layer is still live for its exit animation.
  // So a press that re-opens the dialog from outside it (the recorder pill,
  // shown the instant the modal closes) was delivered as a dismiss of the
  // re-opened dialog and closed it again. A press that began while the dialog
  // was closed can never be a request to close it. (Web only: an RN Modal has
  // no outside-press layer.)
  const openRef = useRef(open);
  openRef.current = open;
  const openAtPressRef = useRef(open);
  useEffect(() => {
    const onPointerDown = () => {
      openAtPressRef.current = openRef.current;
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, []);

  return (
    <RadixDialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay
          // Lets flashka-web's `canShowKaBubble` detect an open modal backdrop
          // (conversational toasts fall back to the system presentation while
          // one is up). Inert everywhere else.
          data-modal-overlay
          className={cn(
            // Frosted scrim, matching the app's dialog treatment:
            // translucent secondary-50 (auto-inverts in dark) + blur.
            // z-[201] sits above app dropdowns/popovers (z-[200]) so dialogs
            // opened from a still-mounted menu render on top.
            'fixed inset-0 z-[201] bg-secondary-50/25 backdrop-blur-[8px] ' +
              'data-[state=open]:animate-in data-[state=open]:fade-in-0 ' +
              'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
            overlayClassName,
          )}
        />
        <RadixDialog.Content
          // No Description element when there's no subheading — silence
          // Radix's missing-description warning the documented way.
          {...(subheading == null ? { 'aria-describedby': undefined } : null)}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => {
            if (!openAtPressRef.current) e.preventDefault();
          }}
          // Must be an inline style: Radix's DismissableLayer force-sets
          // inline `pointer-events: auto` on modal content, which beats the
          // utility class and makes this full-viewport element swallow gutter
          // clicks (outside-dismissal never fires). Our style is spread after
          // Radix's, so it wins; the panel re-enables its own pointer events.
          style={{ pointerEvents: 'none' }}
          className={
            'pointer-events-none fixed inset-0 z-[201] flex flex-col items-center ' +
            'justify-center p-4 outline-none duration-200 ' +
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 ' +
            'data-[state=open]:zoom-in-95 ' +
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 ' +
            'data-[state=closed]:zoom-out-95'
          }>
          {/* Visible title/description render inside DialogSurface with the
              cross-platform primitives; these hidden twins give Radix its
              aria-labelledby / aria-describedby wiring. In composed mode
              (no title) `accessibilityLabel` fills the hidden Title. */}
          <RadixDialog.Title className="sr-only">
            {surface.title ?? accessibilityLabel}
          </RadixDialog.Title>
          {subheading != null && (
            <RadixDialog.Description className="sr-only">
              {subheading}
            </RadixDialog.Description>
          )}
          <DialogSurface
            {...surface}
            subheading={subheading}
            onClosePress={onCloseButtonPress ?? onClose}
            className={className}
            panelClassName={cn(
              'pointer-events-auto max-h-full overflow-y-auto',
              DIALOG_WEB_SHADOW_CLASS,
            )}
          />
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
